import { ACCOUNT_ROUTES } from '../../features/account/paths';
import type { AnchorId } from '@/guidance/anchors';
import type { IconName } from '@/shared/components/Icon';
import { PERMISSIONS, type PermissionSlug } from '../../shared/permissions/permissions';
import type { ReachableSurface } from '@/shared/permissions/useEffectiveAuthority';

export interface NavItem {
  /** Canonical Fidelen glyph. Decorative — every row carries its own text label. */
  icon?: IconName;
  /*
   * The guided-tour anchor for this destination (`tenant.nav.<slug>`, the slug
   * being `to` without its leading slash, `/` read as `-`). A literal per item,
   * so the anchor a tour names is findable in the source. It goes on the
   * sidebar link and on the phone's bottom-navigation link — never on the copy
   * inside the menu sheet, which a tour must not open. `/account/security` has
   * none on purpose: MFA stays outside the tour engine. `/account/profile` has
   * none yet: anchor IDs are registered by the guidance owner only.
   */
  tour?: AnchorId;
  /*
   * Shows a live count beside the label. Only ever set where the number is a
   * real domain fact the API already reports — never a decorative or invented
   * one, and never a second expensive query bought purely to fill a badge.
   */
  badge?: 'approvals' | 'cart';
  to: string;
  labelKey: string;
  permission?: PermissionSlug;
  /*
   * S2B-01 — gate on what the server says this seat may REACH, not on a
   * standing permission. Delegated approval authority is a row with a window
   * and a status; a delegate deliberately holds no `approval.workflow.decide`,
   * so gating on the permission hid the entry from somebody the backend was
   * handing real work to.
   */
  reach?: ReachableSurface;
  anyPermission?: PermissionSlug[];
  /** Absent for a seat holding any of these — it has this destination in another group. */
  unlessAnyPermission?: PermissionSlug[];
  /**
   * What the entry is called for a seat holding any of `anyPermission` — a name
   * for what that seat sees there. Presentation only: it never widens the gate.
   */
  wideLabel?: { anyPermission: PermissionSlug[]; labelKey: string };
  /**
   * IA-05 — other places this entry is the current one for, by path prefix:
   * a request's own pages (`/requests/:id`, its amendment, an off-catalogue
   * need) belong to the request list, which lives at `/my/requests`.
   * Presentation only: it marks where you are, and grants nothing.
   */
  activeFor?: string[];
}

const DESTINATIONS: NavItem[] = [
  { to: '/dashboard', tour: 'tenant.nav.dashboard', icon: 'grid', labelKey: 'nav.dashboard' },
  {
    to: '/catalogue',
    tour: 'tenant.nav.catalogue',
    icon: 'catalog',
    labelKey: 'nav.catalogue',
    permission: PERMISSIONS.catalogueBrowse,
  },
  {
    to: '/cart',
    badge: 'cart',
    tour: 'tenant.nav.cart',
    icon: 'order',
    labelKey: 'nav.cart',
    permission: PERMISSIONS.requestCreate,
  },
  {
    to: '/my/requests',
    tour: 'tenant.nav.my-requests',
    icon: 'document',
    labelKey: 'nav.myRequests',
    wideLabel: { anyPermission: [PERMISSIONS.requestViewAny], labelKey: 'request.title' },
    anyPermission: [PERMISSIONS.requestViewOwn, PERMISSIONS.requestViewAny],
    activeFor: ['/requests'],
  },
  {
    to: '/approvals',
    tour: 'tenant.nav.approvals',
    icon: 'confirm',
    badge: 'approvals',
    labelKey: 'nav.approvals',
    reach: 'approvals',
  },
  {
    to: '/purchase-orders',
    tour: 'tenant.nav.purchase-orders',
    icon: 'order',
    labelKey: 'nav.purchaseOrders',
    // A branch requester holds view_branch and sees what was ordered for
    // them; procurement holds view_any and sees everything.
    anyPermission: [PERMISSIONS.orderViewBranch, PERMISSIONS.orderViewAny],
  },
  {
    to: '/receiving',
    tour: 'tenant.nav.receiving',
    icon: 'package',
    labelKey: 'nav.receiving',
    permission: PERMISSIONS.receiptView,
  },
  {
    to: '/sourcing',
    tour: 'tenant.nav.sourcing',
    icon: 'rfq',
    labelKey: 'nav.sourcing',
    permission: PERMISSIONS.rfqView,
  },
  {
    to: '/suppliers',
    tour: 'tenant.nav.suppliers',
    icon: 'vendor',
    labelKey: 'nav.suppliers',
    permission: PERMISSIONS.supplierView,
  },
  {
    to: '/agreements',
    tour: 'tenant.nav.agreements',
    icon: 'quote',
    labelKey: 'nav.agreements',
    permission: PERMISSIONS.agreementView,
  },
  {
    to: '/reports',
    tour: 'tenant.nav.reports',
    icon: 'document',
    labelKey: 'nav.reports',
    permission: PERMISSIONS.reportView,
  },
  {
    to: '/budgets',
    tour: 'tenant.nav.budgets',
    icon: 'savings',
    labelKey: 'nav.budgets',
    permission: PERMISSIONS.budgetView,
  },
  {
    to: '/admin/approval-rules',
    tour: 'tenant.nav.admin-approval-rules',
    icon: 'confirm',
    labelKey: 'nav.approvalRules',
    permission: PERMISSIONS.approvalRuleView,
  },
  {
    // FND-01 — who the organisation is, as distinct from how it behaves.
    to: '/admin/organization',
    tour: 'tenant.nav.admin-organization',
    icon: 'vendor',
    labelKey: 'nav.organizationProfile',
    permission: PERMISSIONS.tenantView,
  },
  {
    to: '/admin/users',
    tour: 'tenant.nav.admin-users',
    icon: 'user',
    labelKey: 'nav.users',
    permission: PERMISSIONS.userView,
  },
  {
    to: '/admin/roles',
    tour: 'tenant.nav.admin-roles',
    icon: 'lock',
    labelKey: 'nav.roles',
    permission: PERMISSIONS.roleView,
  },
  {
    // The organisation's delegation register; one's own is under the account.
    to: '/admin/delegations',
    tour: 'tenant.nav.admin-delegations',
    icon: 'user',
    labelKey: 'nav.delegations',
    permission: PERMISSIONS.delegationViewAny,
  },
  {
    to: '/admin/settings',
    tour: 'tenant.nav.admin-settings',
    icon: 'settings',
    labelKey: 'nav.settings',
    permission: PERMISSIONS.tenantSettingsView,
  },
  {
    to: '/admin/purchasing-units',
    tour: 'tenant.nav.admin-purchasing-units',
    icon: 'package',
    labelKey: 'nav.purchasingUnits',
    permission: PERMISSIONS.purchasingUnitView,
  },
  {
    to: '/admin/catalogue',
    tour: 'tenant.nav.admin-catalogue',
    icon: 'catalog',
    labelKey: 'nav.catalogueAdmin',
    anyPermission: [PERMISSIONS.catalogueCreate, PERMISSIONS.catalogueUpdate],
  },
  {
    to: '/onboarding',
    tour: 'tenant.nav.onboarding',
    icon: 'download',
    labelKey: 'nav.onboarding',
    permission: PERMISSIONS.onboardingView,
  },
  {
    to: '/admin/audit',
    tour: 'tenant.nav.admin-audit',
    icon: 'document',
    labelKey: 'nav.audit',
    permission: PERMISSIONS.auditView,
  },
  {
    to: '/organization/regions',
    tour: 'tenant.nav.organization-regions',
    icon: 'package',
    labelKey: 'nav.regions',
    permission: PERMISSIONS.regionView,
  },
  {
    to: '/organization/warehouses',
    tour: 'tenant.nav.organization-warehouses',
    icon: 'package',
    labelKey: 'nav.warehouses',
    permission: PERMISSIONS.warehouseView,
  },
  {
    to: '/organization/categories',
    tour: 'tenant.nav.organization-categories',
    icon: 'catalog',
    labelKey: 'nav.categories',
    permission: PERMISSIONS.categoryView,
  },
  {
    to: '/admin/currencies',
    tour: 'tenant.nav.admin-currencies',
    icon: 'savings',
    labelKey: 'nav.currencies',
    permission: PERMISSIONS.currencyView,
  },
  {
    to: '/admin/tax-codes',
    tour: 'tenant.nav.admin-tax-codes',
    icon: 'document',
    labelKey: 'nav.taxCodes',
    permission: PERMISSIONS.taxCodeView,
  },
  { to: ACCOUNT_ROUTES.tenant.profile, icon: 'user', labelKey: 'account.nav.profile' },
  { to: ACCOUNT_ROUTES.tenant.security, icon: 'lock', labelKey: 'nav.signInSecurity' },
  {
    // One's own delegations — the same page, for a seat that is not given
    // the organisation's register under Administration.
    to: '/admin/delegations',
    tour: 'tenant.nav.admin-delegations',
    icon: 'user',
    labelKey: 'nav.delegations',
    permission: PERMISSIONS.delegationViewOwn,
    unlessAnyPermission: [PERMISSIONS.delegationViewAny],
  },
];

export type NavigationModuleId =
  | 'home'
  | 'buying'
  | 'decisions'
  | 'procurement'
  | 'suppliers'
  | 'reports'
  | 'administration'
  | 'master-data'
  | 'account';

export interface NavigationModule {
  id: NavigationModuleId;
  labelKey: string;
  icon: IconName;
  items: NavItem[];
}

const itemsAt = (...paths: string[]): NavItem[] =>
  paths.map((path) => DESTINATIONS.find((item) => item.to === path)!);

/** Work areas, not route changes. Existing policy gates remain on each destination. */
export const TENANT_MODULES: NavigationModule[] = [
  { id: 'home', labelKey: 'navigation.modules.home', icon: 'grid', items: itemsAt('/dashboard') },
  {
    id: 'buying',
    labelKey: 'navigation.modules.buying',
    icon: 'catalog',
    items: itemsAt('/catalogue', '/cart', '/my/requests'),
  },
  {
    id: 'decisions',
    labelKey: 'navigation.modules.decisions',
    icon: 'confirm',
    items: [
      ...itemsAt('/approvals', '/budgets', '/admin/delegations', '/admin/approval-rules'),
      DESTINATIONS.find(
        (item) =>
          item.to === '/admin/delegations' && item.permission === PERMISSIONS.delegationViewOwn,
      )!,
    ],
  },
  {
    id: 'procurement',
    labelKey: 'navigation.modules.procurement',
    icon: 'order',
    items: itemsAt('/sourcing', '/purchase-orders', '/receiving'),
  },
  {
    id: 'suppliers',
    labelKey: 'navigation.modules.suppliers',
    icon: 'vendor',
    items: itemsAt('/suppliers', '/agreements'),
  },
  {
    id: 'reports',
    labelKey: 'navigation.modules.reports',
    icon: 'document',
    items: itemsAt('/reports'),
  },
  {
    id: 'administration',
    labelKey: 'navigation.modules.administration',
    icon: 'settings',
    items: itemsAt(
      '/admin/organization',
      '/admin/users',
      '/admin/roles',
      '/admin/settings',
      '/onboarding',
      '/admin/audit',
    ),
  },
  {
    id: 'master-data',
    labelKey: 'navigation.modules.masterData',
    icon: 'package',
    items: itemsAt(
      '/admin/purchasing-units',
      '/organization/regions',
      '/organization/warehouses',
      '/organization/categories',
      '/admin/catalogue',
      '/admin/currencies',
      '/admin/tax-codes',
    ),
  },
];

/** Personal destinations stay in AccountMenu and Quick Find, outside the primary work areas. */
export const ACCOUNT_MODULE: NavigationModule = {
  id: 'account',
  labelKey: 'nav.account',
  icon: 'user',
  items: itemsAt(ACCOUNT_ROUTES.tenant.profile, ACCOUNT_ROUTES.tenant.security),
};

export interface NavigationAuthority {
  can: (permission: PermissionSlug) => boolean;
  canAny: (permissions: PermissionSlug[]) => boolean;
  canReach: (surface: ReachableSurface) => boolean;
}

export function visibleNavigationModules(authority: NavigationAuthority): NavigationModule[] {
  return TENANT_MODULES.map((module) => ({
    ...module,
    items: module.items.filter((item) => {
      if (item.unlessAnyPermission && authority.canAny(item.unlessAnyPermission)) return false;
      if (item.reach) return authority.canReach(item.reach);
      if (item.permission) return authority.can(item.permission);
      if (item.anyPermission) return authority.canAny(item.anyPermission);
      return true;
    }),
  })).filter((module) => module.items.length > 0);
}

function under(pathname: string, prefix: string): boolean {
  const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** A request detail belongs to its existing request-list destination (IA-05). */
export function isCurrent(item: NavItem, pathname: string): boolean {
  return (
    under(pathname, item.to) || (item.activeFor ?? []).some((prefix) => under(pathname, prefix))
  );
}

/** URL-derived context: native links, deep links and Back/Forward use the same mapping. */
export function navigationModuleForPath(
  pathname: string,
  modules: readonly NavigationModule[] = [...TENANT_MODULES, ACCOUNT_MODULE],
): NavigationModule | undefined {
  return modules.find((module) => module.items.some((item) => isCurrent(item, pathname)));
}
